import { Fragment, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useI18n } from '../i18n';
import { useAuth } from '../context/AuthContext';

const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const mondayOf = (d) => {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  return r;
};

const formatMins = (mins) => `${Math.floor(mins / 60)}h ${pad(mins % 60)}m`;

const emptyForm = { date: isoDate(new Date()), start: '08:00', end: '16:00', location: 'OFFICE', breakMinutes: '', notes: '' };

export default function TimeEntriesView() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const formatTime = (iso) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [team, setTeam] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  const ownView = !selectedUser || Number(selectedUser) === user?.id;

  useEffect(() => {
    api.get('/users/untergebene').then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const params = { date_from: isoDate(weekStart), date_to: isoDate(end) };
    if (selectedUser) params.user_id = selectedUser;
    api.get('/time/overview', { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || t('entries.loadError')))
      .finally(() => setLoading(false));
  }, [weekStart, selectedUser, t]);

  useEffect(() => { load(); }, [load]);

  const shiftWeek = (days) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + days);
    setWeekStart(d);
  };

  const fehlerText = (e) => {
    const detail = e.response?.data?.detail;
    return typeof detail === 'string' ? detail : t('entries.saveError');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      check_in: new Date(`${form.date}T${form.start}`).toISOString(),
      check_out: new Date(`${form.date}T${form.end}`).toISOString(),
      work_location: form.location,
      notes: form.notes || null,
    };
    if (form.breakMinutes !== '') payload.break_minutes = Number(form.breakMinutes);
    try {
      if (editId) await api.put(`/time/entries/${editId}`, payload);
      else await api.post('/time/manual', payload);
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch (err) {
      setError(fehlerText(err));
    }
  };

  const startEdit = (entry) => {
    const von = new Date(entry.check_in);
    const bis = entry.check_out ? new Date(entry.check_out) : null;
    setEditId(entry.id);
    setForm({
      date: isoDate(von),
      start: `${pad(von.getHours())}:${pad(von.getMinutes())}`,
      end: bis ? `${pad(bis.getHours())}:${pad(bis.getMinutes())}` : '',
      location: entry.work_location,
      breakMinutes: entry.break_minutes,
      notes: entry.notes || '',
    });
  };

  const remove = async (id) => {
    if (!window.confirm(t('entries.confirmDelete'))) return;
    try {
      await api.delete(`/time/entries/${id}`);
      load();
    } catch (err) {
      setError(fehlerText(err));
    }
  };

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekSum = data?.weeks?.[0]?.worked_minutes ?? 0;

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <h4 className="mb-0"><i className="bi bi-journal-plus me-2" />{t('entries.title')}</h4>
        {team.length > 0 && (
          <select className="form-select form-select-sm w-auto" value={selectedUser}
                  onChange={(e) => { setSelectedUser(e.target.value); setEditId(null); }}>
            <option value="">{t('entries.ownTimes')}</option>
            {team.map((u) => (
              <option key={u.id} value={u.id}>{u.last_name}, {u.first_name}</option>
            ))}
          </select>
        )}
        <div className="btn-group btn-group-sm ms-auto">
          <button className="btn btn-outline-secondary" onClick={() => shiftWeek(-7)}>&laquo; {t('entries.prevWeek')}</button>
          <button className="btn btn-outline-secondary" onClick={() => setWeekStart(mondayOf(new Date()))}>{t('entries.currentWeek')}</button>
          <button className="btn btn-outline-secondary" onClick={() => shiftWeek(7)}>{t('entries.nextWeek')} &raquo;</button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {ownView && (
        <form className="card card-body mb-4" onSubmit={submit}>
          <h6>{editId ? t('entries.formEdit') : t('entries.formAdd')}</h6>
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small">{t('entries.date')}</label>
              <input type="date" className="form-control form-control-sm" required value={form.date}
                     onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">{t('entries.start')}</label>
              <input type="time" className="form-control form-control-sm" required value={form.start}
                     onChange={(e) => setForm({ ...form, start: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">{t('entries.end')}</label>
              <input type="time" className="form-control form-control-sm" required value={form.end}
                     onChange={(e) => setForm({ ...form, end: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">{t('entries.model')}</label>
              <select className="form-select form-select-sm" value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}>
                <option value="OFFICE">{t('time.office')}</option>
                <option value="HOME_OFFICE">{t('time.homeOffice')}</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">{t('entries.break')}</label>
              <input type="number" min="0" className="form-control form-control-sm" placeholder={t('entries.breakAuto')}
                     value={form.breakMinutes}
                     onChange={(e) => setForm({ ...form, breakMinutes: e.target.value })} />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-primary btn-sm" type="submit">{editId ? t('entries.save') : t('entries.add')}</button>
              {editId && (
                <button className="btn btn-outline-secondary btn-sm" type="button"
                        onClick={() => { setEditId(null); setForm(emptyForm); }}>{t('entries.cancel')}</button>
              )}
            </div>
          </div>
          <input className="form-control form-control-sm mt-2" placeholder={t('entries.notesOptional')} value={form.notes}
                 onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </form>
      )}

      {loading ? (
        <div className="text-center"><div className="spinner-border" /></div>
      ) : data && (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="card text-center"><div className="card-body">
                <div className="fs-4 fw-bold text-primary">{formatMins(weekSum)}</div>
                <div className="text-muted small">
                  {t('entries.weekSum', { from: weekStart.toLocaleDateString(locale), to: weekEnd.toLocaleDateString(locale) })}
                </div>
              </div></div>
            </div>
            <div className="col-md-6">
              <div className="card text-center"><div className="card-body">
                <div className="fs-4 fw-bold text-success">{data.days.length}</div>
                <div className="text-muted small">{t('entries.daysInWeek')}</div>
              </div></div>
            </div>
          </div>

          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>{t('entries.date')}</th><th>{t('entries.start')}</th><th>{t('entries.end')}</th><th>{t('entries.colBreak')}</th><th>{t('entries.colWorked')}</th><th>{t('entries.colModel')}</th><th>{t('entries.colNotes')}</th>
                    {ownView && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.days.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-4">{t('time.noEntries')}</td></tr>
                  ) : data.days.map((day) => (
                    <Fragment key={day.date}>
                      {day.entries.map((en) => (
                        <tr key={en.id}>
                          <td>{new Date(day.date).toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                          <td>{formatTime(en.check_in)}</td>
                          <td>{en.check_out ? formatTime(en.check_out) : <span className="badge bg-success">{t('time.active')}</span>}</td>
                          <td>{t('time.minutes', { n: en.break_minutes })}</td>
                          <td>{formatMins(en.worked_minutes)}</td>
                          <td>
                            {en.work_location === 'HOME_OFFICE'
                              ? <span className="badge bg-info"><i className="bi bi-house me-1" />{t('time.homeOffice')}</span>
                              : <span className="badge bg-secondary"><i className="bi bi-building me-1" />{t('time.office')}</span>}
                          </td>
                          <td><small className="text-muted">{en.notes || ''}</small></td>
                          {ownView && (
                            <td className="text-end">
                              <button className="btn btn-sm btn-outline-primary me-1" onClick={() => startEdit(en)}>
                                <i className="bi bi-pencil" />
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => remove(en.id)}>
                                <i className="bi bi-trash" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      <tr key={`sum-${day.date}`} className="table-light">
                        <td colSpan={4} className="text-end small">{t('entries.daySum')}</td>
                        <td colSpan={ownView ? 4 : 3}><strong>{formatMins(day.worked_minutes)}</strong></td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
